import { Link, createFileRoute, useLoaderData } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { t as ts } from '@/lib/i18n';
import { getServices } from '@/lib/server-fns.ts';
import Section from '@/components/layout/section.tsx';
import PageTitle from '@/components/layout/page-title.tsx';
import Breadcrumbs from '@/components/layout/breadcrumbs.tsx';

const { VITE_LOCALE } = import.meta.env;

export const Route = createFileRoute('/services/')({
	component: Services,
	loader: () => getServices(),
	head: () => ({
		meta: [
			{
				name: 'apple-mobile-web-app-title',
				content: `Jervice - ${ts('global.menu.services')}`,
			},
			{
				name: 'og:title',
				content: `Jervice - ${ts('global.menu.services')}`,
			},
			{
				name: 'og:description',
				content: ts('services.page_caption'),
			},
			{
				name: 'og:url',
				content: `https://jervice.${VITE_LOCALE === 'en' ? 'eu' : 'pl'}/services`,
			},
			{
				name: 'og:image',
				content: `/images/main.png`,
			},
			{
				name: 'twitter:title',
				content: `Jervice - ${ts('global.menu.services')}`,
			},
			{
				name: 'twitter:description',
				content: ts('services.page_caption'),
			},
			{
				name: 'twitter:url',
				content: `https://jervice.${VITE_LOCALE === 'en' ? 'eu' : 'pl'}/services`,
			},
			{
				name: 'twitter:image',
				content: `/images/main.png`,
			},
			{ title: `Jervice - ${ts('global.menu.services')}` },
		],
	}),
});

function Services() {
	const { t } = useTranslation();
	const pageData = useLoaderData({ from: '/services/' });

	return (
		<Section className="flex flex-col gap-8 py-6 max-md:py-4">
			<Breadcrumbs
				items={[{ title: t('global.menu.home'), link: '/' }, { title: t('services.all_services') }]}
			/>

			<div className="flex flex-col gap-2">
				<PageTitle className="text-[var(--primary)]">{t('services.all_services')}</PageTitle>

				<p className="text-lg max-lg:text-[16px] text-neutral-500">{t('services.page_caption')}</p>
			</div>

			<div className="grid grid-cols-12 gap-4">
				{pageData?.map((service: any, i: number) => (
					<div
						key={`service_${service?.id}_${i}`}
						className={`col-span-4 max-2xl:col-span-4 max-xl:col-span-6 max-lg:col-span-6 max-md:col-span-12 h-full flex flex-col`}
					>
						<Link
							to="/services/$serviceId"
							params={{
								serviceId: service?.id,
							}}
							className="services-item-link rounded-xl relative"
						>
							<span
								className="w-full flex-1 rounded-xl overflow-hidden service-item"
								style={{
									viewTransitionName: `service-image-${service.id}`,
								}}
							>
								<span className="w-full h-full service-image">
									<img
										src={service.photoUrl}
										alt={service?.name}
										className="w-full rounded-xl object-cover aspect-video"
									/>
								</span>

								<span className="service-item-info flex flex-col justify-center gap-4 absolute rounded-xl top-0 left-0 w-full h-full bg-gray-900/40 backdrop-blur-[1px] text-white p-4 z-10">
									<h2 className="text-white/90 text-center text-4xl max-2xl:text-3xl line-clamp-3">
										{service?.name}
									</h2>
								</span>
							</span>
						</Link>
					</div>
				))}
			</div>
		</Section>
	);
}
